// src/Features/Quotes/utils/pdfGenerator.js
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import logo from '../../../assets/images/logo.png';
import { toast } from '../../../components/Toast';

/**
 * Safety net so the literal strings "null" / "undefined" never leak into the PDF.
 * Applied as a didParseCell callback on every autoTable below.
 */
const SAFE_CELL_HOOK = (data) => {
  const raw = data?.cell?.raw;
  if (raw === null || raw === undefined) {
    data.cell.text = [''];
    return;
  }
  if (typeof raw === 'string' && (raw === 'null' || raw === 'undefined')) {
    data.cell.text = [''];
  }
};

// Wrapper around autoTable that always installs SAFE_CELL_HOOK while preserving
// any didParseCell the caller passes in. Use this everywhere instead of autoTable.
const safeAutoTable = (doc, options) => {
  const userHook = options && options.didParseCell;
  return autoTable(doc, {
    ...options,
    didParseCell: (data) => {
      SAFE_CELL_HOOK(data);
      if (typeof userHook === 'function') userHook(data);
    }
  });
};

/**
 * Remove columns where every row has an empty / blank value.
 * columnStyles: object keyed by original column index.
 * Returns { filteredHeaders, filteredData, filteredStyles }.
 */
function filterEmptyColumns(headers, tableData, columnStyles = {}) {
  const keepIndices = headers
    .map((_, colIdx) =>
      tableData.some(row => {
        const val = row[colIdx];
        return val !== '' && val !== null && val !== undefined && String(val).trim() !== '';
      })
    )
    .map((keep, i) => (keep ? i : -1))
    .filter(i => i >= 0);

  const filteredHeaders = keepIndices.map(i => headers[i]);
  const filteredData = tableData.map(row => keepIndices.map(i => row[i]));

  // Remap column styles to the new sequential indices
  const filteredStyles = {};
  keepIndices.forEach((origIdx, newIdx) => {
    if (columnStyles[origIdx]) {
      filteredStyles[newIdx] = columnStyles[origIdx];
    }
  });

  return { filteredHeaders, filteredData, filteredStyles };
}

/**
 * Add Air Freight Charges Table for Transit/MultiModal (Pivoted by Carrier)
 */
function addAirFreightChargesTableTransit(doc, chargesData, yPos, segmentNum, tableName = null) {
  // Handle both old format (direct array) and new format (tables with charges)
  let charges = [];
  if (Array.isArray(chargesData)) {
    if (chargesData.length > 0 && chargesData[0].charges) {
      // New format: array of tables
      chargesData.forEach(table => {
        if (table.charges) {
          charges = charges.concat(table.charges);
        }
      });
    } else {
      // Old format or direct array
      charges = chargesData;
    }
  }
  
  const validCharges = charges.filter(c => c.carrier || c.unitType || c.amount);
  
  if (validCharges.length === 0) {
    return yPos;
  }
  
  if (yPos > 230) {
    doc.addPage();
    yPos = 20;
  }
  
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  const title = tableName ? `Freight Charges - ${tableName} (Segment ${segmentNum})` : `Freight Charges (Segment ${segmentNum})`;
  doc.text(title, 15, yPos);
  yPos += 3;
  
  // Collect all unique unit types and sort them
  const allUnitTypes = new Set();
  validCharges.forEach(charge => {
    if (charge.unitType) {
      allUnitTypes.add(charge.unitType);
    }
  });
  
  // Convert to sorted array - try to sort numerically for weight breaks
  const unitTypeColumns = Array.from(allUnitTypes).sort((a, b) => {
    // Try to extract numbers for sorting
    const numA = parseFloat(a.replace(/[^0-9.-]/g, ''));
    const numB = parseFloat(b.replace(/[^0-9.-]/g, ''));
    if (!isNaN(numA) && !isNaN(numB)) {
      return numA - numB;
    }
    return a.localeCompare(b);
  });
  
  // If no unit types found, use standard ones
  if (unitTypeColumns.length === 0) {
    unitTypeColumns.push('-45', '45', '100', '300', '500', '1000');
  }

  // Filter out unit types where no carrier has a non-zero amount
  const filledUnitTypes = unitTypeColumns.filter(ut => {
    return validCharges.some(c => {
      if (c.unitType !== ut) return false;
      const amt = parseFloat(c.amount);
      return amt && amt > 0;
    });
  });
  // Use filtered list if there are filled ones, otherwise keep all (fallback)
  const displayUnitTypes = filledUnitTypes.length > 0 ? filledUnitTypes : unitTypeColumns;

  // Group charges by carrier
  const carrierGroups = {};
  validCharges.forEach(charge => {
    const carrier = charge.carrier || '';
    if (!carrierGroups[carrier]) {
      carrierGroups[carrier] = {
        carrier: carrier,
        currency: charge.currency || '',
        minimum: charge.minimum || '',
        unitTypes: {},
        surcharge: charge.surcharge || '',
        transitTime: charge.transitTime || '',
        frequency: charge.frequency || '',
        routing: charge.numberOfRouting || '',
        remarks: formatRemarksWithBreaks(charge.remarks || '')
      };
    }

    // Store amount by unit type
    const unitType = charge.unitType || '';
    if (unitType) {
      carrierGroups[carrier].unitTypes[unitType] = charge.amount || '';
    }
  });

  // Extract common info across all carriers to show once above the table
  const groupValues = Object.values(carrierGroups);
  const commonInfo = [];
  // Check if transit time is common
  const transitTimes = [...new Set(groupValues.map(g => g.transitTime).filter(Boolean))];
  if (transitTimes.length > 0) commonInfo.push({ label: 'Transit Time', value: transitTimes.join(', ') });
  // Check if frequency is common
  const frequencies = [...new Set(groupValues.map(g => g.frequency).filter(Boolean))];
  if (frequencies.length > 0) commonInfo.push({ label: 'Frequency', value: frequencies.join(', ') });
  // Check if routing is common
  const routings = [...new Set(groupValues.map(g => g.routing).filter(Boolean))];
  if (routings.length > 0) commonInfo.push({ label: 'Routing', value: routings.join(', ') });
  // Check if remarks are common
  const remarksList = [...new Set(groupValues.map(g => g.remarks).filter(Boolean))];
  if (remarksList.length > 0) commonInfo.push({ label: 'Remarks', value: remarksList.join(', ') });

  // Show common info as text above the table
  if (commonInfo.length > 0) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    commonInfo.forEach(info => {
      if (yPos > 270) { doc.addPage(); yPos = 20; }
      doc.setFont('helvetica', 'bold');
      doc.text(`${info.label}: `, 15, yPos);
      const labelWidth = doc.getTextWidth(`${info.label}: `);
      doc.setFont('helvetica', 'normal');
      // Wrap long text
      const maxWidth = 165;
      const lines = doc.splitTextToSize(info.value, maxWidth);
      lines.forEach((line, i) => {
        doc.text(line, 15 + (i === 0 ? labelWidth : 0), yPos);
        if (i < lines.length - 1) yPos += 3.5;
      });
      yPos += 4;
    });
    yPos += 1;
  }

  // Build table data - clean table without transit/frequency/routing/remarks columns
  const tableData = Object.values(carrierGroups).map(group => {
    const row = [
      group.carrier,
      group.currency,
      group.minimum
    ];

    // Add only filled unit type values
    displayUnitTypes.forEach(unitType => {
      row.push(group.unitTypes[unitType] || '');
    });

    // Add surcharges only
    row.push(group.surcharge);

    return row;
  });

  // Build headers - without T/T, FREQUENCY, ROUTING, REMARKS
  const headers = ['AIRLINE', 'CCY', 'M'];
  headers.push(...displayUnitTypes);
  headers.push('SURCHARGES');

  // Calculate column widths dynamically based on number of unit types
  const numUnitTypes = displayUnitTypes.length;
  const unitTypeWidth = numUnitTypes > 6 ? 12 : 15;

  const columnStyles = {
    0: { cellWidth: 20 },  // AIRLINE
    1: { cellWidth: 12 },  // CCY
    2: { cellWidth: 12 }   // M
  };

  // Unit type columns
  for (let i = 0; i < numUnitTypes; i++) {
    columnStyles[3 + i] = { cellWidth: unitTypeWidth };
  }

  // Surcharges column
  columnStyles[3 + numUnitTypes] = { cellWidth: 20 };  // SURCHARGES

  safeAutoTable(doc, {
    startY: yPos,
    head: [headers],
    body: tableData,
    theme: 'grid',
    headStyles: { fillColor: [200, 200, 200], textColor: 0, fontSize: 6.5, fontStyle: 'bold' },
    bodyStyles: { fontSize: 6.5, overflow: 'linebreak', cellPadding: 1.5 },
    columnStyles: columnStyles,
    margin: { left: 15, right: 15 }
  });

  return doc.lastAutoTable.finalY + 8;
}

/**
 * Add Freight Charges Table for Transit/MultiModal
 * Handles freightChargesTables format with different field names
 */
function addFreightChargesTableTransit(doc, charges, yPos, isAir, segmentNum, tableName = null) {
  
  // Check if charges have carrier/unitType format or chargeableWeight format
  const hasCarrierFormat = charges.some(c => c.carrier || c.unitType || c.amount);
  const hasWeightFormat = charges.some(c => c.chargeableWeight || c.weightBreaker || c.charge);
  
  
  // Use specialized Air table ONLY for air freight with carrier/unitType format
  // If data has chargeableWeight format, use regular table even for air mode
  if (isAir && hasCarrierFormat && !hasWeightFormat) {
    return addAirFreightChargesTableTransit(doc, charges, yPos, segmentNum, tableName);
  }
  
  
  // Handle carrier/unitType format (like Direct quotes)
  if (hasCarrierFormat) {
    const validCharges = charges.filter(c => c.carrier || c.unitType || c.amount);

    if (validCharges.length === 0) {
      return yPos;
    }

    if (yPos > 230) {
      doc.addPage();
      yPos = 20;
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    const title = tableName ? `Freight Charges - ${tableName} (Segment ${segmentNum})` : `Freight Charges (Segment ${segmentNum})`;
    doc.text(title, 15, yPos);
    yPos += 3;

    // For air mode: show common info (carrier, transit, routing, remarks) as text above a simplified table
    if (isAir) {
      // Extract common info
      const commonInfoAir = [];
      const carriers = [...new Set(validCharges.map(c => c.carrier).filter(Boolean))];
      if (carriers.length > 0) commonInfoAir.push({ label: 'Carrier', value: carriers.join(', ') });
      const transits = [...new Set(validCharges.map(c => c.transitTime).filter(Boolean))];
      if (transits.length > 0) commonInfoAir.push({ label: 'Transit Time', value: transits.join(', ') });
      const routingsAir = [...new Set(validCharges.map(c => c.numberOfRouting).filter(Boolean))];
      if (routingsAir.length > 0) commonInfoAir.push({ label: 'Routing', value: routingsAir.join(', ') });
      const remarksAir = [...new Set(validCharges.map(c => c.remarks).filter(Boolean))];
      if (remarksAir.length > 0) commonInfoAir.push({ label: 'Remarks', value: remarksAir.join(', ') });

      // Show common info as text
      if (commonInfoAir.length > 0) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        commonInfoAir.forEach(info => {
          if (yPos > 270) { doc.addPage(); yPos = 20; }
          doc.setFont('helvetica', 'bold');
          doc.text(`${info.label}: `, 15, yPos);
          const labelW = doc.getTextWidth(`${info.label}: `);
          doc.setFont('helvetica', 'normal');
          const maxW = 165;
          const lns = doc.splitTextToSize(info.value, maxW);
          lns.forEach((ln, i) => {
            doc.text(ln, 15 + (i === 0 ? labelW : 0), yPos);
            if (i < lns.length - 1) yPos += 3.5;
          });
          yPos += 4;
        });
        yPos += 1;
      }

      // Filter out rows with zero or empty amounts - only show unit types with actual data
      const filledCharges = validCharges.filter(charge => {
        const amt = parseFloat(charge.amount);
        return amt && amt > 0;
      });
      // Use filtered if any have data, otherwise fallback to all
      const displayCharges = filledCharges.length > 0 ? filledCharges : validCharges;

      // Simplified table: Unit Type, Amount only
      const airTableData = displayCharges.map(charge => {
        const currency = charge.currency || '';
        const amount = charge.amount || '';
        const formattedAmount = currency && amount ? `${currency} ${amount}` : amount;
        return [
          charge.unitType || '',
          formattedAmount
        ];
      });

      const airHeaders = ['Unit Type', 'Amount'];

      safeAutoTable(doc, {
        startY: yPos,
        head: [airHeaders],
        body: airTableData,
        theme: 'grid',
        headStyles: { fillColor: [200, 200, 200], textColor: 0, fontSize: 7, fontStyle: 'bold' },
        bodyStyles: { fontSize: 6.5, overflow: 'linebreak', cellPadding: 1.5 },
        columnStyles: {
          0: { cellWidth: 40 },  // Unit Type
          1: { cellWidth: 40 }   // Amount (with currency)
        },
        margin: { left: 15, right: 15 }
      });

      return doc.lastAutoTable.finalY + 8;
    }

    // Filter out rows with zero or empty amounts - only show unit types with actual data
    const filledChargesGeneral = validCharges.filter(charge => {
      const amt = parseFloat(charge.amount);
      return amt && amt > 0;
    });
    // Use filtered if any have data, otherwise fallback to all
    const displayChargesGeneral = filledChargesGeneral.length > 0 ? filledChargesGeneral : validCharges;

    const tableData = displayChargesGeneral.map(charge => {
      const units = charge.numberOfUnits || '';
      const total = calculateChargeTotal(charge);
      const currency = charge.currency || '';
      const amount = charge.amount || '';
      const formattedAmount = currency && amount ? `${currency} ${amount}` : amount;
      const fmtTotal = formatCurrencyAmount(total);
      const formattedTotal = currency ? `${currency} ${fmtTotal}` : fmtTotal;

      const row = [
        charge.carrier || '',
        charge.unitType || '',
        units,
        formattedAmount,
        charge.transitTime || '',
        charge.numberOfRouting || ''
      ];

      row.push(formattedTotal);
      row.push(formatRemarksWithBreaks(charge.remarks || ''));
      return row;
    });

    const headers = ['Carrier', 'Unit Type', 'Units', 'Amount', 'Transit', 'Routing', 'Total', 'Remarks'];

    const transitCarrierColStyles = {
      0: { cellWidth: 16 },  // Carrier
      1: { cellWidth: 13 },  // Unit Type
      2: { cellWidth: 8 },   // Units
      3: { cellWidth: 25 },  // Amount (with currency)
      4: { cellWidth: 12 },  // Transit
      5: { cellWidth: 14 },  // Routing
      6: { cellWidth: 24 },  // Total (with currency)
      7: { cellWidth: 50, overflow: 'linebreak', fontSize: 6.5 }  // Remarks
    };
    const { filteredHeaders: tcH, filteredData: tcD, filteredStyles: tcS } =
      filterEmptyColumns(headers, tableData, transitCarrierColStyles);

    safeAutoTable(doc, {
      startY: yPos,
      head: [tcH],
      body: tcD,
      theme: 'grid',
      headStyles: { fillColor: [200, 200, 200], textColor: 0, fontSize: 7, fontStyle: 'bold' },
      bodyStyles: { fontSize: 6.5, overflow: 'linebreak', cellPadding: 1.5 },
      columnStyles: tcS,
      margin: { left: 15, right: 15 }
    });

    return doc.lastAutoTable.finalY + 8;
  }

  // Handle chargeableWeight format (original Transit format)
  if (hasWeightFormat) {
    const validCharges = charges.filter(c => c.chargeableWeight || c.weightBreaker || c.charge);

    if (validCharges.length === 0) {
      return yPos;
    }

    if (yPos > 230) {
      doc.addPage();
      yPos = 20;
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    const title = tableName ? `Freight Charges - ${tableName} (Segment ${segmentNum})` : `Freight Charges (Segment ${segmentNum})`;
    doc.text(title, 15, yPos);
    yPos += 3;
    
    const tableData = validCharges.map(charge => {
      // Calculate total: chargeableWeight * charge
      const weight = parseFloat(charge.chargeableWeight) || 0;
      const rate = parseFloat(charge.charge) || 0;
      const total = weight * rate;
      const currency = charge.currency || 'USD';
      const chargeAmount = charge.charge || '';
      const formattedCharge = chargeAmount ? `${currency} ${chargeAmount}` : '';
      const formattedTotal = `${currency} ${formatCurrencyAmount(total)}`;

      return [
        charge.chargeableWeight || '',
        charge.weightBreaker || '',
        charge.pricingUnit || '',
        formattedCharge,
        formattedTotal,
        formatRemarksWithBreaks(charge.remark || charge.remarks || '')
      ];
    });

    const weightHeaders = ['Chargeable Weight', 'Weight Breaker', 'Pricing Unit', 'Charge', 'Total', 'Remark'];
    const weightColStyles = {
      0: { cellWidth: 22 },
      1: { cellWidth: 22 },
      2: { cellWidth: 22 },
      3: { cellWidth: 28 },  // Charge (with currency)
      4: { cellWidth: 28 },  // Total (with currency)
      5: { cellWidth: 48, overflow: 'linebreak' }  // Remark
    };
    const { filteredHeaders: wH, filteredData: wD, filteredStyles: wS } =
      filterEmptyColumns(weightHeaders, tableData, weightColStyles);

    safeAutoTable(doc, {
      startY: yPos,
      head: [wH],
      body: wD,
      theme: 'grid',
      headStyles: { fillColor: [200, 200, 200], textColor: 0, fontSize: 7, fontStyle: 'bold' },
      bodyStyles: { fontSize: 6.5, overflow: 'linebreak', cellPadding: 1.5 },
      columnStyles: wS,
      margin: { left: 15, right: 15 }
    });
    
    return doc.lastAutoTable.finalY + 8;
  }
  
  // No valid charges found
  return yPos;
}

/**
 * Calculate total for transit/multimodal quotes with new structure
 * Includes: Origin Handling + Destination Handling
 * Excludes: Freight Charges
 */
function calculateTransitTotalNew(segments) {
  let total = 0;
  
  segments.forEach(segment => {
    // Origin handling
    if (segment.originHandling) {
      segment.originHandling.forEach(charge => {
        total += calculateChargeTotal(charge);
      });
    }
    
    // Destination handling
    if (segment.destinationHandling) {
      segment.destinationHandling.forEach(charge => {
        total += calculateChargeTotal(charge);
      });
    }
    
    // NOTE: Freight charges are NOT included in total
  });
  
  return total;
}

/**
 * Get currency from transit/multimodal segments (from origin or destination handling)
 */
function getSegmentsCurrency(segments) {
  for (const segment of segments) {
    // Check destination handling first
    if (segment.destinationHandling && segment.destinationHandling.length > 0) {
      const currency = segment.destinationHandling[0]?.currency;
      if (currency) return currency;
    }
    // Then check origin handling
    if (segment.originHandling && segment.originHandling.length > 0) {
      const currency = segment.originHandling[0]?.currency;
      if (currency) return currency;
    }
  }
  return 'USD'; // Default to USD
}

/**
 * Format remarks text with line breaks for better display
 * Splits at numbered points (01., 02., etc.) and long sections
 */
/**
 * Add Air Freight Ratio Charges Table to PDF
 * Renders horizontal table with ratio columns: AIRLINE, CCY, 1:167, 1:200, 1:300, 1:400, 1:500, SURCHARGE, T/T, FREQUENCY, ROUTING, REMARKS
 */
function addAirFreightRatioChargesTable(doc, charges, yPos, title = 'Air Freight Ratio Charges') {
  const validCharges = charges.filter(c => c.carrier || c.ratio || c.amount);

  if (validCharges.length === 0) {
    return yPos;
  }

  if (yPos > 230) {
    doc.addPage();
    yPos = 20;
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text(title, 15, yPos);
  yPos += 3;

  // Collect all unique ratios and sort them
  const standardRatios = ['1:167', '1:200', '1:300', '1:400', '1:500'];
  const allRatios = new Set();
  validCharges.forEach(charge => {
    if (charge.ratio) {
      allRatios.add(charge.ratio);
    }
  });

  const ratioColumns = allRatios.size > 0 ? Array.from(allRatios).sort((a, b) => {
    const numA = parseFloat(a.split(':')[1] || '0');
    const numB = parseFloat(b.split(':')[1] || '0');
    return numA - numB;
  }) : standardRatios;

  // Group charges by carrier
  const carrierGroups = {};
  validCharges.forEach(charge => {
    const carrier = charge.carrier || '';
    if (!carrierGroups[carrier]) {
      carrierGroups[carrier] = {
        carrier: carrier,
        currency: charge.currency || '',
        ratios: {},
        surcharge: charge.surcharge || '',
        transitTime: charge.transitTime || '',
        frequency: charge.frequency || '',
        routing: charge.numberOfRouting || '',
        remarks: formatRemarksWithBreaks(charge.remarks || '')
      };
    }

    const ratio = charge.ratio || '';
    if (ratio) {
      carrierGroups[carrier].ratios[ratio] = charge.amount || '';
    }
  });

  // Build table data
  const tableData = Object.values(carrierGroups).map(group => {
    const row = [group.carrier, group.currency];

    ratioColumns.forEach(ratio => {
      row.push(group.ratios[ratio] || '');
    });

    row.push(group.surcharge);
    row.push(group.transitTime);
    row.push(group.frequency);
    row.push(group.routing);
    row.push(group.remarks);

    return row;
  });

  // Build headers
  const headers = ['AIRLINE', 'Currency', ...ratioColumns, 'SURCHARGE', 'T/T', 'FREQUENCY', 'ROUTING', 'REMARKS'];

  // Calculate column widths
  const numRatios = ratioColumns.length;
  const ratioWidth = numRatios > 5 ? 10 : 12;

  const columnStyles = {
    0: { cellWidth: 15 },  // AIRLINE
    1: { cellWidth: 10 }   // CCY
  };

  for (let i = 0; i < numRatios; i++) {
    columnStyles[2 + i] = { cellWidth: ratioWidth };
  }

  columnStyles[2 + numRatios] = { cellWidth: 16 };      // SURCHARGE
  columnStyles[3 + numRatios] = { cellWidth: 10 };      // T/T
  columnStyles[4 + numRatios] = { cellWidth: 13 };      // FREQUENCY
  columnStyles[5 + numRatios] = { cellWidth: 15 };      // ROUTING
  columnStyles[6 + numRatios] = { cellWidth: 28, overflow: 'linebreak' };  // REMARKS

  const { filteredHeaders: ratioH, filteredData: ratioD, filteredStyles: ratioS } =
    filterEmptyColumns(headers, tableData, columnStyles);

  safeAutoTable(doc, {
    startY: yPos,
    head: [ratioH],
    body: ratioD,
    theme: 'grid',
    headStyles: { fillColor: [200, 200, 200], textColor: 0, fontSize: 6.5, fontStyle: 'bold' },
    bodyStyles: { fontSize: 6.5, overflow: 'linebreak', cellPadding: 1.5 },
    columnStyles: ratioS,
    margin: { left: 15, right: 15 }
  });

  return doc.lastAutoTable.finalY + 8;
}

/**
 * Check if a charge amount is meaningful (non-empty, non-zero)
 */
function isMeaningfulAmount(amount) {
  if (!amount) return false;
  const num = parseFloat(amount);
  return !isNaN(num) && num !== 0;
}

/**
 * Check if ratio charges have actual data filled in
 * Returns true if any row has carrier AND at least one meaningful amount
 */
function hasRatioChargesData(ratioCharges) {
  if (!ratioCharges || !Array.isArray(ratioCharges) || ratioCharges.length === 0) return false;
  return ratioCharges.some(c => c.carrier && isMeaningfulAmount(c.amount));
}

/**
 * Check if standard freight charges have actual data filled in
 * For air freight: checks carrier + meaningful amount (ignores unitType-only rows with 0.00 amounts)
 * Works with both freightChargesTables and freightCharges formats
 */
function hasFreightChargesData(option) {
  const hasMeaningfulCharge = (c) => {
    // A charge has real data if it has a carrier with a meaningful amount
    if (c.carrier && isMeaningfulAmount(c.amount)) return true;
    // Or for sea format: chargeableWeight based
    if (c.chargeableWeight || c.weightBreaker) return true;
    return false;
  };

  // Check freightChargesTables format
  const freightTables = option.freightChargesTables || [];
  if (freightTables.length > 0) {
    const hasTableData = freightTables.some(t =>
      (t.charges || []).some(hasMeaningfulCharge)
    );
    if (hasTableData) return true;
  }
  // Check old freightCharges format
  const freightCharges = option.freightCharges || [];
  if (freightCharges.length > 0) {
    return freightCharges.some(hasMeaningfulCharge);
  }
  return false;
}

const formatRemarksWithBreaks = (remarks) => {
  if (!remarks || remarks.trim() === '') return '';

  let formatted = remarks.trim();

  // NEW: Handle common freight remarks separated by /
  // Example: "ETD: 26-JAN / VESSEL: ... / SPACE - TIGHT"
  // → becomes multi-line with each section on its own line
  const parts = formatted.split(/\s*\/\s*/);
  const cleanedParts = parts
    .map(part => part.trim())
    .filter(part => part !== '');
  formatted = cleanedParts.join('\n/ ');

  // Existing breaks (kept unchanged)
  formatted = formatted.replace(/(\d{2}\.\s)/g, '\n$1');

  formatted = formatted.replace(/^\n/, '');

  if (formatted.length > 60) {
    formatted = formatted.replace(/(\.\s)(?=\D)/g, '$1\n');
    formatted = formatted.replace(/\n{3,}/g, '\n\n');
  }

  return formatted.trim();
};

/**
 * Extract charges from table structures used in Transit routes
 * Converts location-based table format to charge array format
 */
const extractChargesFromTables = (tables, chargeType = 'handling') => {
  
  if (!tables || tables.length === 0) {
    return [];
  }
  
  const allCharges = [];
  
  tables.forEach((table, idx) => {
    
    // Handle Transit format: freight charges with nested charges array
    if (table.charges && Array.isArray(table.charges)) {
      allCharges.push(...table.charges);
      return;
    }
    
    // Handle MultiModal format: freight charges with direct fields (no nested charges array)
    if (chargeType === 'freight' && (table.chargeableWeight || table.weightBreaker || table.charge)) {
      // This is a direct freight charge object (MultiModal format)
      allCharges.push(table);
      return;
    }
    
    // Handle handling/destination charges (location-based format)
    const locations = Object.keys(table.chargeNamePerLocation || {});

    locations.forEach(location => {
      const chargeName = table.chargeNamePerLocation?.[location];
      const unitType = table.unitTypePerLocation?.[location];
      const units = table.unitsPerLocation?.[location];
      const amount = table.amountPerLocation?.[location];
      const currency = table.currencyPerLocation?.[location];
      const remark = table.remarkPerLocation?.[location];

      // Only add if there's actual data
      if (chargeName || amount) {
        allCharges.push({
          chargeName: chargeName || '',
          unitType: unitType || '',
          numberOfUnits: units || '',
          amount: amount || '',
          currency: currency || '',
          total: 0,
          remark: remark || ''
        });
      }
    });
  });
  
  return allCharges;
};

/**
 * Calculate total for a charge
 */
const calculateChargeTotal = (charge) => {
  // If total is already calculated and non-zero, use it
  if (charge.total && parseFloat(charge.total) > 0) {
    return parseFloat(charge.total);
  }
  
  // Calculate: numberOfUnits * amount
  const units = parseFloat(charge.numberOfUnits) || 1; // Default to 1 if empty
  const amount = parseFloat(charge.amount) || 0;
  
  return units * amount;
};

/**
 * Format amount based on currency
 * Show decimals only when needed (e.g., 2075.50, 18000)
 */
const formatCurrencyAmount = (amount, currency) => {
  const val = parseFloat(amount) || 0;
  return val % 1 === 0 ? val.toFixed(0) : val.toFixed(2);
};

/**
 * Common header for all PDFs
 */
const addPDFHeader = (doc, quoteData) => {
  let yPos = 15;
  
  // Logo - maintaining aspect ratio (original: 367 x 72, ratio 5.097:1)
  doc.addImage(logo, 'PNG', 15, yPos, 50, 10);
  
  // Company Info
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('CRM System', 195, yPos + 2, { align: 'right' });
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('67/1 Hudson Road Colombo 3 Sri Lanka.', 195, yPos + 7, { align: 'right' });
  doc.text('Office #+94 11 2426600/4766400', 195, yPos + 12, { align: 'right' });
  
  yPos += 25;
  
  // Title
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('QUOTATION', 105, yPos, { align: 'center' });
  
  return yPos + 12;
};

/**
 * Add customer section
 */
const addCustomerSection = (doc, quoteData, yPos) => {
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(quoteData.quoteNumber || 'N/A', 15, yPos);
  
  const isAir = quoteData.freightCategory === 'air';
  const freightLabel = isAir ? 'Air' : 'Sea';
  const modeLabel = quoteData.freightMode?.charAt(0).toUpperCase() + quoteData.freightMode?.slice(1);
  const typeLabel = quoteData.freightType?.toUpperCase();
  const typeText = `${freightLabel} ${modeLabel} ${typeLabel}`;
  
  doc.text(typeText, 195, yPos, { align: 'right' });
  
  yPos += 5;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('Credit Terms', 195, yPos, { align: 'right' });
  
  if (quoteData.rateValidity) {
    yPos += 4;
    const validityDate = new Date(quoteData.rateValidity).toLocaleDateString('en-GB');
    doc.text(`Rate Validity: ${validityDate}, 05:30`, 195, yPos, { align: 'right' });
  }
  
  yPos += 10;

  // Customer and Address
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.text('Customer', 15, yPos);

  // Add Customer Address label if address exists
  if (quoteData.customerAddress && quoteData.customerAddress.trim() !== '') {
    doc.text('Customer Address', 130, yPos);
  }

  doc.setFont('helvetica', 'normal');
  yPos += 4;

  const customerNameYPos = yPos;
  doc.text(quoteData.customer || 'N/A', 15, yPos);

  // Customer Address - display to the right of customer name
  let addressLineCount = 0;
  if (quoteData.customerAddress && quoteData.customerAddress.trim() !== '') {
    doc.setFont('helvetica', 'normal');
    const addressLines = doc.splitTextToSize(quoteData.customerAddress, 70);
    addressLines.forEach((line, index) => {
      doc.text(line, 130, customerNameYPos + (index * 4));
    });
    addressLineCount = addressLines.length;
  }

  // Advance yPos by the maximum of customer name line or address lines
  const maxLines = Math.max(1, addressLineCount);
  yPos += (maxLines * 4);

  // Contact Name - display below customer name if exists
  if (quoteData.contactName && quoteData.contactName.trim() !== '') {
    doc.setFont('helvetica', 'bold');
    doc.text('Contact Person', 15, yPos);
    doc.setFont('helvetica', 'normal');
    yPos += 4;
    doc.text(quoteData.contactName, 15, yPos);
    yPos += 4;
  }

  yPos += 4;

  // Addresses
  doc.setFont('helvetica', 'bold');
  doc.text('Pickup Address', 15, yPos);
  doc.text('Delivery Address', 130, yPos);
  doc.setFont('helvetica', 'normal');
  yPos += 4;

  const pickupText = quoteData.pickupLocation || 'N/A';
  const deliveryText = quoteData.deliveryLocation || 'N/A';

  const pickupLines = doc.splitTextToSize(pickupText, 110);    // width for left side
  const deliveryLines = doc.splitTextToSize(deliveryText, 70); // width for right side

  const n_maxLines = Math.max(pickupLines.length, deliveryLines.length, 1);

  for (let i = 0; i < n_maxLines; i++) {
    const lineY = yPos + (i * 4);
    if (i < pickupLines.length) {
      doc.text(pickupLines[i], 15, lineY);
    }
    if (i < deliveryLines.length) {
      doc.text(deliveryLines[i], 130, lineY);
    }
  }

  yPos += (maxLines * 4) + 4;  // dynamic spacing (same as original when 1 line)
  
  return yPos;
};

/**
 * Generate PDF for Direct Quotes
 */
export const generateDirectQuotePDF = (quoteData, userData = null, returnDoc = false) => {
  const doc = new jsPDF();
  let yPos = addPDFHeader(doc, quoteData);
  
  yPos = addCustomerSection(doc, quoteData, yPos);
  
  // Parse data - handle both old and new formats
  const equipment = quoteData.equipment ? JSON.parse(quoteData.equipment) : {};
  const terms = quoteData.termsConditions ? JSON.parse(quoteData.termsConditions) : [];
  const isAir = quoteData.freightCategory === 'air';
  
  // Check for new carrierOptions format
  let carrierOptions = [];
  if (quoteData.carrierOptions) {
    carrierOptions = JSON.parse(quoteData.carrierOptions);
  } else {
    // Old format - convert to new format
    const carriers = quoteData.carriers ? JSON.parse(quoteData.carriers) : [];
    const freightCharges = quoteData.freightCharges ? JSON.parse(quoteData.freightCharges) : [];
    const destinationCharges = quoteData.destinationCharges ? JSON.parse(quoteData.destinationCharges) : [];
    const originHandling = quoteData.originHandling ? JSON.parse(quoteData.originHandling) : [];
    const destinationHandling = quoteData.destinationHandling ? JSON.parse(quoteData.destinationHandling) : [];
    
    // Create single option from old format
    if (carriers.length > 0 || freightCharges.length > 0) {
      carrierOptions = [{
        carrier: carriers[0]?.carrier || '',
        incoterm: carriers[0]?.incoterm || '',
        currency: carriers[0]?.currency || '',
        cargoType: carriers[0]?.cargoType || '',
        freightCharges: freightCharges,
        destinationCharges: destinationCharges,
        originHandling: originHandling,
        destinationHandling: destinationHandling
      }];
    }
  }
  
  // Line separator
  doc.line(15, yPos, 195, yPos);
  yPos += 5;
  
  // Port Details
  doc.setFont('helvetica', 'bold');
  doc.text('Port of Loading', 15, yPos);
  doc.text('Port of Discharge', 130, yPos);
  doc.setFont('helvetica', 'normal');
  yPos += 4;
  doc.text(quoteData.portOfLoading || 'N/A', 15, yPos);
  doc.text(quoteData.portOfDischarge || 'N/A', 130, yPos);
  
  yPos += 8;
  
  // Equipment
  doc.setFont('helvetica', 'bold');
  doc.text('Equipment', 15, yPos);
  doc.text('Units', 70, yPos);
  doc.text('Gross Weight', 95, yPos);
  doc.text('Volume (m3)', 135, yPos);
  doc.text('Chargeable Weight', 165, yPos);
  
  yPos += 4;
  doc.setFont('helvetica', 'normal');
  doc.text(equipment.equipment || 'N/A', 15, yPos);
  doc.text(equipment.units || '0', 70, yPos);
  doc.text(equipment.grossWeight || '0', 95, yPos);
  doc.text(equipment.volume || '0', 135, yPos);
  doc.text(equipment.chargeableWeight || '0', 165, yPos);
  
  yPos += 10;
  
  // Carrier Options or Old Format Charges
  if (carrierOptions.length > 0) {
    carrierOptions.forEach((option, optionIndex) => {
      if (yPos > 240) {
        doc.addPage();
        yPos = 20;
      }
      
      // Option Header (only if multiple options)
      if (carrierOptions.length > 1) {
        doc.setFillColor(230, 230, 230);
        doc.rect(15, yPos - 3, 180, 7, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.text(`${isAir ? 'Airline' : 'Carrier'} Option [${optionIndex + 1}]`, 18, yPos + 2);
        yPos += 10;
      }
      
      // Carrier Details (if exists)
      if (option.carrier || option.incoterm || option.currency || option.cargoType) {
        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        yPos += 2;
        
        doc.setFont('helvetica', 'normal');
        let detailsText = [];
        if (!isAir && option.carrier) detailsText.push(`Carrier: ${option.carrier}`);
        if (option.incoterm) detailsText.push(`Incoterm: ${option.incoterm}`);
        if (option.currency) detailsText.push(`Currency: ${option.currency}`);
        if (option.cargoType) detailsText.push(`Cargo Type: ${option.cargoType}`);
        
        if (detailsText.length > 0) {
          doc.text(detailsText.join('  |  '), 15, yPos);
          yPos += 8;
        }
      }

      // Freight Charges - Mutually exclusive with Ratio Charges for air category
      // If ratio charges have data, show ratio table. Otherwise show standard freight table.
      const optionHasRatioData = isAir && hasRatioChargesData(option.seaFreightRatioCharges);
      const optionHasFreightData = hasFreightChargesData(option);

      if (isAir && optionHasRatioData && !optionHasFreightData) {
        // Show ONLY ratio charges table
        yPos = addAirFreightRatioChargesTable(doc, option.seaFreightRatioCharges, yPos);
      } else {
        // Show standard freight charges table (default for non-air, or when freight data exists)
        if (option.freightChargesTables && Array.isArray(option.freightChargesTables) && option.freightChargesTables.length > 0) {
          // New format: multiple tables
          const hasMultipleTables = option.freightChargesTables.length > 1 ||
                                     (option.freightChargesTables[0]?.tableName && option.freightChargesTables[0]?.tableName !== 'Default');

          if (hasMultipleTables) {
            // Render each table separately with table name
            option.freightChargesTables.forEach((table, tableIndex) => {
              const tableCharges = table.charges || [];
              if (tableCharges.length > 0) {
                const tableName = table.tableName || `Table ${tableIndex + 1}`;
                yPos = addFreightChargesTableWithName(doc, tableCharges, yPos, isAir, tableName, optionIndex + 1);
              }
            });
          } else {
            // Single table - render without table name
            const charges = option.freightChargesTables[0]?.charges || [];
            yPos = addFreightChargesTable(doc, charges, yPos, isAir);
          }
        } else if (option.freightCharges) {
          // Old format: direct array
          yPos = addFreightChargesTable(doc, option.freightCharges || [], yPos, isAir);
        }
      }

      // Destination Charges
      yPos = addOtherChargesTable(doc, option.destinationCharges || [], 'Destination Charges (POD)', yPos);
      
      // Origin Handling
      yPos = addOtherChargesTable(doc, option.originHandling || [], 'Origin Handling Charges', yPos);
      
      // Destination Handling
      yPos = addOtherChargesTable(doc, option.destinationHandling || [], 'Destination Handling Charges', yPos);
      
    });
  }
  
  // Terms & Conditions
  if (terms.length > 0) {
    if (yPos > 240) {
      doc.addPage();
      yPos = 20;
    }
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('Terms & Conditions:', 15, yPos);
    yPos += 5;
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    terms.forEach(term => {
      const lines = doc.splitTextToSize(`- ${term}`, 170);
      lines.forEach(line => {
        if (yPos > 280) {
          doc.addPage();
          yPos = 20;
        }
        doc.text(line, 15, yPos);
        yPos += 4;
      });
    });
  }
  
  // Footer
  yPos = 280;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'italic');
  
  // Format user information
  const userName = userData ? `${userData.firstName || ''} ${userData.lastName || ''}`.trim() : 'System';
  const userEmail = userData?.email ? ` (${userData.email})` : '';
  
  doc.text(`Quotation generated by - ${userName}${userEmail}`, 15, yPos);
  doc.text('This is a Computer generated Document and no Signature required.', 15, yPos + 4);
  
  // Save
  if (returnDoc) {
    return doc;
  }
  doc.save(`${quoteData.quoteNumber || 'Quote'}.pdf`);
};

/**
 * Add Air Freight Charges Table with optional table name (Pivoted by Carrier and Unit Type)
 */
function addAirFreightChargesTableWithName(doc, charges, yPos, tableName = null, optionNum = null) {
  const validCharges = charges.filter(c => c.carrier || c.unitType || c.amount);

  if (validCharges.length === 0) {
    return yPos;
  }

  if (yPos > 230) {
    doc.addPage();
    yPos = 20;
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  const title = tableName
    ? `Freight Charges - ${tableName} (Option ${optionNum})`
    : `Freight Charges (Option ${optionNum})`;
  doc.text(title, 15, yPos);
  yPos += 3;

  // Collect all unique unit types and sort them
  const allUnitTypes = new Set();
  validCharges.forEach(charge => {
    if (charge.unitType) {
      allUnitTypes.add(charge.unitType);
    }
  });

  // Convert to sorted array
  const unitTypeColumns = Array.from(allUnitTypes).sort((a, b) => {
    const numA = parseFloat(a.replace(/[^0-9.-]/g, ''));
    const numB = parseFloat(b.replace(/[^0-9.-]/g, ''));
    if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
    return a.localeCompare(b);
  });

  // If no unit types found, use standard ones
  if (unitTypeColumns.length === 0) {
    unitTypeColumns.push('-45', '45', '100', '300', '500', '1000');
  }

  // Group charges by carrier
  const carrierGroups = {};
  validCharges.forEach(charge => {
    const carrier = charge.carrier || '';
    if (!carrierGroups[carrier]) {
      carrierGroups[carrier] = {
        carrier: carrier,
        currency: charge.currency || '',
        minimum: charge.minimum || '',
        unitTypes: {},
        surcharge: charge.surcharge || '',
        transitTime: charge.transitTime || '',
        frequency: charge.frequency || '',
        routing: charge.numberOfRouting || '',
        remarks: formatRemarksWithBreaks(charge.remarks || '')
      };
    }

    // Store amount by unit type
    const unitType = charge.unitType || '';
    if (unitType) {
      carrierGroups[carrier].unitTypes[unitType] = charge.amount || '';
    }
  });

  // Build table data
  const tableData = [];
  Object.values(carrierGroups).forEach(group => {
    const row = [group.carrier, group.currency];

    // Add amounts for each unit type column
    unitTypeColumns.forEach(unitType => {
      row.push(group.unitTypes[unitType] || '');
    });

    row.push(
      group.minimum,
      group.surcharge,
      group.transitTime,
      group.frequency,
      group.routing,
      group.remarks
    );

    tableData.push(row);
  });

  // Build headers
  const headers = ['AIRLINE', 'Currency', ...unitTypeColumns, 'MIN', 'SURCH', 'T/T', 'FREQ', 'ROUTING', 'REMARKS'];

  // Build columnStyles for filtering
  const numUT = unitTypeColumns.length;
  const utWidth = numUT > 6 ? 10 : 12;
  const airWithNameColStyles = { 0: { cellWidth: 15 }, 1: { cellWidth: 10 } };
  for (let i = 0; i < numUT; i++) airWithNameColStyles[2 + i] = { cellWidth: utWidth };
  airWithNameColStyles[2 + numUT] = { cellWidth: 12 };  // MIN
  airWithNameColStyles[3 + numUT] = { cellWidth: 14 };  // SURCH
  airWithNameColStyles[4 + numUT] = { cellWidth: 10 };  // T/T
  airWithNameColStyles[5 + numUT] = { cellWidth: 13 };  // FREQ
  airWithNameColStyles[6 + numUT] = { cellWidth: 15 };  // ROUTING
  airWithNameColStyles[7 + numUT] = { cellWidth: 28, overflow: 'linebreak' };  // REMARKS

  const { filteredHeaders: awnH, filteredData: awnD, filteredStyles: awnS } =
    filterEmptyColumns(headers, tableData, airWithNameColStyles);

  safeAutoTable(doc, {
    startY: yPos,
    head: [awnH],
    body: awnD,
    theme: 'grid',
    headStyles: { fillColor: [200, 200, 200], textColor: 0, fontSize: 6.5, fontStyle: 'bold' },
    bodyStyles: { fontSize: 6.5, overflow: 'linebreak', cellPadding: 1.5 },
    columnStyles: awnS,
    margin: { left: 15, right: 15 }
  });

  return doc.lastAutoTable.finalY + 8;
}

/**
 * Add Air Freight Charges Table (Pivoted by Carrier and Unit Type)
 */
function addAirFreightChargesTable(doc, charges, yPos) {
  const validCharges = charges.filter(c => c.carrier || c.unitType || c.amount);

  if (validCharges.length === 0) {
    return yPos;
  }
  
  if (yPos > 230) {
    doc.addPage();
    yPos = 20;
  }
  
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('Freight Charges', 15, yPos);
  yPos += 3;
  
  // Collect all unique unit types and sort them
  const allUnitTypes = new Set();
  validCharges.forEach(charge => {
    if (charge.unitType) {
      allUnitTypes.add(charge.unitType);
    }
  });
  
  // Convert to sorted array - try to sort numerically for weight breaks
  const unitTypeColumns = Array.from(allUnitTypes).sort((a, b) => {
    // Try to extract numbers for sorting
    const numA = parseFloat(a.replace(/[^0-9.-]/g, ''));
    const numB = parseFloat(b.replace(/[^0-9.-]/g, ''));
    if (!isNaN(numA) && !isNaN(numB)) {
      return numA - numB;
    }
    return a.localeCompare(b);
  });
  
  // If no unit types found, use standard ones
  if (unitTypeColumns.length === 0) {
    unitTypeColumns.push('-45', '45', '100', '300', '500', '1000');
  }
  
  // Group charges by carrier
  const carrierGroups = {};
  validCharges.forEach(charge => {
    const carrier = charge.carrier || '';
    if (!carrierGroups[carrier]) {
      carrierGroups[carrier] = {
        carrier: carrier,
        currency: charge.currency || '',
        minimum: charge.minimum || '',
        unitTypes: {},
        surcharge: charge.surcharge || '',
        transitTime: charge.transitTime || '',
        frequency: charge.frequency || '',
        routing: charge.numberOfRouting || '',
        remarks: formatRemarksWithBreaks(charge.remarks || '')
      };
    }

    // Store amount by unit type
    const unitType = charge.unitType || '';
    if (unitType) {
      carrierGroups[carrier].unitTypes[unitType] = charge.amount || '';
    }
  });
  
  // Build table data
  const tableData = Object.values(carrierGroups).map(group => {
    const row = [
      group.carrier,
      group.currency,
      group.minimum
    ];
    
    // Add unit type values
    unitTypeColumns.forEach(unitType => {
      row.push(group.unitTypes[unitType] || '');
    });
    
    // Add remaining columns
    row.push(group.surcharge);
    row.push(group.transitTime);
    row.push(group.frequency);
    row.push(group.routing);
    row.push(group.remarks);
    
    return row;
  });
  
  // Build headers
  const headers = ['AIRLINE', 'Currency', 'M'];
  headers.push(...unitTypeColumns);
  headers.push('SURCHARGES', 'T/T', 'FREQUENCY', 'ROUTING', 'REMARKS');

  // Calculate column widths dynamically based on number of unit types
  const numUnitTypes = unitTypeColumns.length;
  const unitTypeWidth = numUnitTypes > 6 ? 10 : 12;

  const columnStyles = {
    0: { cellWidth: 15 },  // AIRLINE
    1: { cellWidth: 10 },  // CCY
    2: { cellWidth: 10 }   // M
  };

  // Unit type columns
  for (let i = 0; i < numUnitTypes; i++) {
    columnStyles[3 + i] = { cellWidth: unitTypeWidth };
  }

  // Remaining columns
  columnStyles[3 + numUnitTypes] = { cellWidth: 16 };  // SURCHARGES
  columnStyles[4 + numUnitTypes] = { cellWidth: 10 };  // T/T
  columnStyles[5 + numUnitTypes] = { cellWidth: 13 };  // FREQUENCY
  columnStyles[6 + numUnitTypes] = { cellWidth: 15 };  // ROUTING
  columnStyles[7 + numUnitTypes] = { cellWidth: 28, overflow: 'linebreak' };  // REMARKS - much wider

  const { filteredHeaders: airH, filteredData: airD, filteredStyles: airS } =
    filterEmptyColumns(headers, tableData, columnStyles);

  safeAutoTable(doc, {
    startY: yPos,
    head: [airH],
    body: airD,
    theme: 'grid',
    headStyles: { fillColor: [200, 200, 200], textColor: 0, fontSize: 6.5, fontStyle: 'bold' },
    bodyStyles: { fontSize: 6.5, overflow: 'linebreak', cellPadding: 1.5 },
    columnStyles: airS,
    margin: { left: 15, right: 15 }
  });

  return doc.lastAutoTable.finalY + 8;
}

/**
 * Add Freight Charges Table with optional table name
 */
function addFreightChargesTableWithName(doc, charges, yPos, isAir, tableName = null, optionNum = null) {
  // Use specialized Air table for air freight
  if (isAir) {
    return addAirFreightChargesTableWithName(doc, charges, yPos, tableName, optionNum);
  }

  // Filter out empty charges
  const validCharges = charges.filter(c => c.carrier || c.unitType || c.amount);

  if (validCharges.length === 0) {
    return yPos;
  }

  if (yPos > 230) {
    doc.addPage();
    yPos = 20;
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  const title = tableName
    ? `Freight Charges - ${tableName} (Option ${optionNum})`
    : `Freight Charges (Option ${optionNum})`;
  doc.text(title, 15, yPos);
  yPos += 3;

  const tableData = validCharges.map(charge => {
    const units = charge.numberOfUnits || '';
    const total = calculateChargeTotal(charge);
    const currency = charge.currency || '';
    const amount = charge.amount || '';
    const formattedAmount = currency && amount ? `${currency} ${amount}` : amount;
    const fmtTotal = formatCurrencyAmount(total);
    const formattedTotal = currency ? `${currency} ${fmtTotal}` : fmtTotal;

    const row = [
      charge.carrier || '',
      charge.unitType || '',
      units,
      formattedAmount,
      charge.transitTime || '',
      charge.numberOfRouting || ''
    ];

    row.push(formattedTotal);
    row.push(formatRemarksWithBreaks(charge.remarks || ''));
    return row;
  });

  const headers = ['Carrier', 'Unit Type', 'Units', 'Amount', 'Transit', 'Routing', 'Total', 'Remarks'];

  const fctwnColStyles = {
    0: { cellWidth: 16 },  // Carrier
    1: { cellWidth: 13 },  // Unit Type
    2: { cellWidth: 8 },   // Units
    3: { cellWidth: 25 },  // Amount (with currency)
    4: { cellWidth: 12 },  // Transit
    5: { cellWidth: 14 },  // Routing
    6: { cellWidth: 24 },  // Total (with currency)
    7: { cellWidth: 50, overflow: 'linebreak', fontSize: 6.5 }  // Remarks
  };
  const { filteredHeaders: fctwnH, filteredData: fctwnD, filteredStyles: fctwnS } =
    filterEmptyColumns(headers, tableData, fctwnColStyles);

  safeAutoTable(doc, {
    startY: yPos,
    head: [fctwnH],
    body: fctwnD,
    theme: 'grid',
    headStyles: { fillColor: [200, 200, 200], textColor: 0, fontSize: 7, fontStyle: 'bold' },
    bodyStyles: { fontSize: 6.5, overflow: 'linebreak', cellPadding: 1.5 },
    columnStyles: fctwnS,
    margin: { left: 15, right: 15 }
  });

  return doc.lastAutoTable.finalY + 8;
}

/**
 * Add Freight Charges Table
 */
function addFreightChargesTable(doc, charges, yPos, isAir) {
  // Use specialized Air table for air freight
  if (isAir) {
    return addAirFreightChargesTable(doc, charges, yPos);
  }

  // Filter out empty charges
  const validCharges = charges.filter(c => c.carrier || c.unitType || c.amount);

  if (validCharges.length === 0) {
    return yPos;
  }

  if (yPos > 230) {
    doc.addPage();
    yPos = 20;
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('Freight Charges', 15, yPos);
  yPos += 3;
  
  const tableData = validCharges.map(charge => {
    const units = charge.numberOfUnits || '';
    const total = calculateChargeTotal(charge);
    const currency = charge.currency || '';
    const amount = charge.amount || '';
    const formattedAmount = currency && amount ? `${currency} ${amount}` : amount;
    const fmtTotal = formatCurrencyAmount(total);
    const formattedTotal = currency ? `${currency} ${fmtTotal}` : fmtTotal;

    const row = [
      charge.carrier || '',
      charge.unitType || '',
      units,
      formattedAmount,
      charge.transitTime || '',
      charge.numberOfRouting || ''
    ];

    row.push(formattedTotal);
    row.push(formatRemarksWithBreaks(charge.remarks || ''));
    return row;
  });
  
  const headers = ['Carrier', 'Unit Type', 'Units', 'Amount', 'Transit', 'Routing', 'Total', 'Remarks'];

  const fctColStyles = {
    0: { cellWidth: 16 },  // Carrier
    1: { cellWidth: 13 },  // Unit Type
    2: { cellWidth: 8 },   // Units
    3: { cellWidth: 25 },  // Amount (with currency)
    4: { cellWidth: 12 },  // Transit
    5: { cellWidth: 14 },  // Routing
    6: { cellWidth: 24 },  // Total (with currency)
    7: { cellWidth: 50, overflow: 'linebreak', fontSize: 6.5 }  // Remarks
  };
  const { filteredHeaders: fctH, filteredData: fctD, filteredStyles: fctS } =
    filterEmptyColumns(headers, tableData, fctColStyles);

  safeAutoTable(doc, {
    startY: yPos,
    head: [fctH],
    body: fctD,
    theme: 'grid',
    headStyles: { fillColor: [200, 200, 200], textColor: 0, fontSize: 7, fontStyle: 'bold' },
    bodyStyles: { fontSize: 6.5, overflow: 'linebreak', cellPadding: 1.5 },
    columnStyles: fctS,
    margin: { left: 15, right: 15 }
  });

  return doc.lastAutoTable.finalY + 8;
}

/**
 * Add Other Charges Table (Destination, Origin Handling, etc.)
 */
function addOtherChargesTable(doc, charges, title, yPos) {
  // Filter out empty charges
  const validCharges = charges.filter(c => c.chargeName || c.amount);
  
  if (validCharges.length === 0) {
    return yPos;
  }
  
  if (yPos > 230) {
    doc.addPage();
    yPos = 20;
  }
  
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text(title, 15, yPos);
  yPos += 3;
  
  const tableData = validCharges.map(charge => {
    const units = charge.numberOfUnits || (charge.unitType === 'SHIPMENT' ? '1' : '');
    const total = calculateChargeTotal(charge);
    const currency = charge.currency || '';
    const amount = charge.amount || '';
    const formattedAmount = currency && amount ? `${currency} ${amount}` : amount;
    const fmtTotal = formatCurrencyAmount(total);
    const formattedTotal = currency ? `${currency} ${fmtTotal}` : fmtTotal;
    
    return [
      charge.chargeName || '',
      charge.unitType || '',
      units,
      formattedAmount,
      formattedTotal,
      formatRemarksWithBreaks(charge.remark || '')
    ];
  });
  
  const octHeaders = ['Charge Name', 'Unit Type', 'Units', 'Amount', 'Total', 'Remark'];
  const octColStyles = {
    0: { cellWidth: 36 },
    1: { cellWidth: 22 },
    2: { cellWidth: 12 },
    3: { cellWidth: 28 },  // Amount (with currency)
    4: { cellWidth: 28 },  // Total (with currency)
    5: { cellWidth: 44, overflow: 'linebreak' }  // Remark
  };
  const { filteredHeaders: octH, filteredData: octD, filteredStyles: octS } =
    filterEmptyColumns(octHeaders, tableData, octColStyles);

  safeAutoTable(doc, {
    startY: yPos,
    head: [octH],
    body: octD,
    theme: 'grid',
    headStyles: { fillColor: [200, 200, 200], textColor: 0, fontSize: 7, fontStyle: 'bold' },
    bodyStyles: { fontSize: 6.5, overflow: 'linebreak', cellPadding: 1.5 },
    columnStyles: octS,
    margin: { left: 15, right: 15 }
  });

  return doc.lastAutoTable.finalY + 8;
}

/**
 * Calculate option total
 */
function calculateOptionTotal(option) {
  let total = 0;
  
  // Destination charges (POD)
  if (option.destinationCharges) {
    option.destinationCharges.forEach(charge => {
      total += calculateChargeTotal(charge);
    });
  }
  
  // Origin handling
  if (option.originHandling) {
    option.originHandling.forEach(charge => {
      total += calculateChargeTotal(charge);
    });
  }
  
  // Destination handling
  if (option.destinationHandling) {
    option.destinationHandling.forEach(charge => {
      total += calculateChargeTotal(charge);
    });
  }
  
  // NOTE: Freight charges are NOT included in total
  
  return total;
}

/**
 * Generate PDF for Transit Quotes
 */
export const generateTransitQuotePDF = (quoteData, userData = null, returnDoc = false) => {
  const doc = new jsPDF();
  let yPos = addPDFHeader(doc, quoteData);
  
  yPos = addCustomerSection(doc, quoteData, yPos);
  
  const equipment = quoteData.equipment ? JSON.parse(quoteData.equipment) : {};
  const terms = quoteData.termsConditions ? JSON.parse(quoteData.termsConditions) : [];
  
  // Parse routes - handle nested structure
  let routesData = [];
  let allSegments = [];
  
  // Try TransitRoutes first, then routes
  const routesField = quoteData.transitRoutes || quoteData.routes;
  if (routesField) {
    const parsedRoutes = JSON.parse(routesField);
    if (Array.isArray(parsedRoutes) && parsedRoutes.length > 0) {
      // Direct array of routes
      routesData = parsedRoutes;
      allSegments = parsedRoutes;
      
      // OR Extract routes array from nested structure
      if (parsedRoutes[0].routes) {
        routesData = parsedRoutes[0].routes;
        allSegments = parsedRoutes[0].routes;
      }
    }
  }
  
  doc.line(15, yPos, 195, yPos);
  yPos += 5;
  
  // Route Segments Display
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('Route Segments', 15, yPos);
  yPos += 5;
  
  doc.setFont('helvetica', 'normal');
  if (routesData.length > 0) {
    // Build route including transit ports
    const firstRoute = routesData[0];
    let routeSegments = [firstRoute?.origin || 'N/A'];
    if (firstRoute?.transits && firstRoute.transits.length > 0) {
      routeSegments.push(...firstRoute.transits);
    }
    routeSegments.push(firstRoute?.destination || routesData[routesData.length - 1]?.destination || 'N/A');
    const routeText = routeSegments.join(' -> ');
    const routeLines = doc.splitTextToSize(routeText, 170);
    routeLines.forEach(line => {
      doc.text(line, 15, yPos);
      yPos += 4;
    });
  }

  yPos += 6;

  // Equipment (top level or first segment)
  const topEquipment = equipment.equipment ? equipment : (allSegments[0]?.equipment || {});
  doc.setFont('helvetica', 'bold');
  doc.text('Equipment', 15, yPos);
  doc.text('Units', 70, yPos);
  doc.text('Gross Weight', 95, yPos);
  doc.text('Volume (m3)', 135, yPos);
  doc.text('Chargeable Weight', 165, yPos);

  yPos += 4;
  doc.setFont('helvetica', 'normal');
  doc.text(topEquipment.equipment || equipment.equipment || 'N/A', 15, yPos);
  doc.text(equipment.units || topEquipment.units || '0', 70, yPos);
  doc.text(topEquipment.grossWeight || equipment.grossWeight || '0', 95, yPos);
  doc.text(topEquipment.volume || equipment.volume || '0', 135, yPos);
  doc.text(topEquipment.chargeableWeight || equipment.chargeableWeight || '0', 165, yPos);

  yPos += 10;

  // Process each segment
  allSegments.forEach((segment, index) => {
    if (yPos > 240) {
      doc.addPage();
      yPos = 20;
    }

    doc.setFillColor(200, 220, 240);
    doc.rect(15, yPos - 3, 180, 7, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);

    const segmentMode = segment.mode?.toUpperCase() || quoteData.freightCategory?.toUpperCase() || 'N/A';
    // Build segment route including transit ports
    let segRouteArr = [segment.origin || 'N/A'];
    if (segment.transits && segment.transits.length > 0) {
      segRouteArr.push(...segment.transits);
    }
    segRouteArr.push(segment.destination || 'N/A');
    const segmentRoute = segRouteArr.join(' -> ');
    const segHeaderText = `Segment ${index + 1}: ${segmentMode} (${segmentRoute})`;
    const segHeaderLines = doc.splitTextToSize(segHeaderText, 174);
    segHeaderLines.forEach((line, li) => {
      if (li > 0) {
        doc.rect(15, yPos - 3, 180, 5, 'F');
      }
      doc.text(line, 18, yPos + 2);
      yPos += li === 0 ? 7 : 5;
    });

    // Incoterm display
    const carriers = segment.carriers || [];
    const incoterms = carriers.map(c => c.incoterm).filter(Boolean);
    if (incoterms.length > 0) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text(`Incoterm: ${incoterms.join(', ')}`, 18, yPos);
      yPos += 5;
    }
    yPos += 3;

    // Freight Charges - Mutually exclusive with Ratio Charges for air segments
    // Check if ratio tables have actual data (carrier + meaningful amount)
    const segRatioTables = segment.seaFreightRatioChargesTables || [];
    const segHasRatioData = segment.mode === 'air' && segRatioTables.some(t =>
      (t.charges || []).some(c => c.carrier && isMeaningfulAmount(c.amount))
    );
    // Check if standard freight tables have actual meaningful data
    const segHasFreightData = hasFreightChargesData(segment);

    if (segment.mode === 'air' && segHasRatioData && !segHasFreightData) {
      // Show ONLY ratio charges table for this air segment
      segRatioTables.forEach((table) => {
        const ratioCharges = table.charges || [];
        if (ratioCharges.length > 0) {
          const tableName = table.tableName || 'Default';
          yPos = addAirFreightRatioChargesTable(doc, ratioCharges, yPos, `Air Freight Ratio Charges - ${tableName} (Segment ${index + 1})`);
        }
      });
    } else {
      // Show standard freight charges table
      if (segment.freightChargesTables && Array.isArray(segment.freightChargesTables) && segment.freightChargesTables.length > 0) {
        const hasMultipleTables = segment.freightChargesTables.length > 1 ||
                                   (segment.freightChargesTables[0]?.tableName && segment.freightChargesTables[0]?.tableName !== 'Default');

        if (hasMultipleTables) {
          segment.freightChargesTables.forEach((table, tableIndex) => {
            const tableCharges = table.charges || [];
            if (tableCharges.length > 0) {
              const tableName = table.tableName || `Table ${tableIndex + 1}`;
              yPos = addFreightChargesTableTransit(doc, tableCharges, yPos, segment.mode === 'air', index + 1, tableName);
            }
          });
        } else {
          const freightCharges = extractChargesFromTables(segment.freightChargesTables, 'freight');
          if (freightCharges.length > 0) {
            yPos = addFreightChargesTableTransit(doc, freightCharges, yPos, segment.mode === 'air', index + 1);
          }
        }
      } else if (segment.freightCharges) {
        const freightCharges = segment.freightCharges;
        if (freightCharges.length > 0) {
          yPos = addFreightChargesTableTransit(doc, freightCharges, yPos, segment.mode === 'air', index + 1);
        }
      }
    }

    // Origin Handling - extract from originHandlingTables
    let originHandling = [];
    if (segment.originHandlingTables && Array.isArray(segment.originHandlingTables)) {
      originHandling = extractChargesFromTables(segment.originHandlingTables, 'handling');
    } else if (segment.originHandling) {
      originHandling = segment.originHandling;
    }
    if (originHandling.length > 0) {
      yPos = addOtherChargesTable(doc, originHandling, `Seg${index + 1}: Origin Handling`, yPos);
    }

    // Destination Handling - extract from destinationHandlingTables
    let destinationHandling = [];
    if (segment.destinationHandlingTables && Array.isArray(segment.destinationHandlingTables)) {
      destinationHandling = extractChargesFromTables(segment.destinationHandlingTables, 'handling');
    } else if (segment.destinationHandling) {
      destinationHandling = segment.destinationHandling;
    }
    if (destinationHandling.length > 0) {
      yPos = addOtherChargesTable(doc, destinationHandling, `Seg${index + 1}: Destination Handling`, yPos);
    }

    // Destination Charges (POD) - extract from destinationChargesTables
    let destinationCharges = [];
    if (segment.destinationChargesTables && Array.isArray(segment.destinationChargesTables)) {
      destinationCharges = extractChargesFromTables(segment.destinationChargesTables, 'handling');
    } else if (segment.destinationCharges) {
      destinationCharges = segment.destinationCharges;
    }
    if (destinationCharges.length > 0) {
      yPos = addOtherChargesTable(doc, destinationCharges, `Seg${index + 1}: Destination Charges (POD)`, yPos);
    }

    yPos += 5;
  });


  // Terms
  if (terms.length > 0) {
    yPos += 10;
    if (yPos > 250) {
      doc.addPage();
      yPos = 20;
    }
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('Terms & Conditions:', 15, yPos);
    yPos += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    terms.forEach(term => {
      const lines = doc.splitTextToSize(`- ${term}`, 170);
      lines.forEach(line => {
        if (yPos > 280) {
          doc.addPage();
          yPos = 20;
        }
        doc.text(line, 15, yPos);
        yPos += 4;
      });
    });
  }

  // Footer
  const finalY = 280;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'italic');

  // Format user information
  const userName = userData ? `${userData.firstName || ''} ${userData.lastName || ''}`.trim() : 'System';
  const userEmail = userData?.email ? ` (${userData.email})` : '';

  doc.text(`Quotation generated by - ${userName}${userEmail}`, 15, finalY);
  doc.text('This is a Computer generated Document and no Signature required.', 15, finalY + 4);

  if (returnDoc) {
    return doc;
  }
  doc.save(`${quoteData.quoteNumber || 'Transit-Quote'}.pdf`);
};

/**
 * Generate PDF for MultiModal Quotes
 */
export const generateMultiModalQuotePDF = (quoteData, userData = null, returnDoc = false) => {
  const doc = new jsPDF();
  let yPos = addPDFHeader(doc, quoteData);
  
  yPos = addCustomerSection(doc, quoteData, yPos);
  
  const equipment = quoteData.equipment ? JSON.parse(quoteData.equipment) : {};
  const terms = quoteData.termsConditions ? JSON.parse(quoteData.termsConditions) : [];
  
  // Parse routes - handle nested structure (same as transit)
  let routesData = [];
  let allSegments = [];
  
  // Try TransitRoutes first, then routes
  const routesField = quoteData.transitRoutes || quoteData.routes;
  if (routesField) {
    const parsedRoutes = JSON.parse(routesField);
    if (Array.isArray(parsedRoutes) && parsedRoutes.length > 0) {
      // Direct array of routes
      routesData = parsedRoutes;
      allSegments = parsedRoutes;
      
      // OR Extract routes array from nested structure
      if (parsedRoutes[0].routes) {
        routesData = parsedRoutes[0].routes;
        allSegments = parsedRoutes[0].routes;
      }
    }
  }
  
  doc.line(15, yPos, 195, yPos);
  yPos += 5;
  
  // Route Segments Display
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('Route Segments', 15, yPos);
  yPos += 5;
  
  doc.setFont('helvetica', 'normal');
  if (routesData.length > 0) {
    const routeText = routesData.map((r, idx) => {
      const mode = r.mode?.toUpperCase() || 'N/A';
      const route = `${r.origin || 'N/A'} -> ${r.destination || 'N/A'}`;
      return `Seg ${idx + 1}: ${mode} (${route})`;
    }).join('  |  ');

    const routeLines = doc.splitTextToSize(routeText, 170);
    routeLines.forEach(line => {
      doc.text(line, 15, yPos);
      yPos += 4;
    });
  }

  yPos += 6;

  // Equipment (top level or first segment)
  const topEquipment = equipment.equipment ? equipment : (allSegments[0]?.equipment || {});
  doc.setFont('helvetica', 'bold');
  doc.text('Equipment', 15, yPos);
  doc.text('Units', 70, yPos);
  doc.text('Gross Weight', 95, yPos);
  doc.text('Volume (m3)', 135, yPos);
  doc.text('Chargeable Weight', 165, yPos);

  yPos += 4;
  doc.setFont('helvetica', 'normal');
  doc.text(topEquipment.equipment || equipment.equipment || 'N/A', 15, yPos);
  doc.text(equipment.units || topEquipment.units || '0', 70, yPos);
  doc.text(topEquipment.grossWeight || equipment.grossWeight || '0', 95, yPos);
  doc.text(topEquipment.volume || equipment.volume || '0', 135, yPos);
  doc.text(topEquipment.chargeableWeight || equipment.chargeableWeight || '0', 165, yPos);

  yPos += 10;

  // Process each segment
  allSegments.forEach((segment, index) => {
    if (yPos > 240) {
      doc.addPage();
      yPos = 20;
    }

    doc.setFillColor(200, 220, 240);
    doc.rect(15, yPos - 3, 180, 7, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);

    const segmentMode = segment.mode?.toUpperCase() || quoteData.freightCategory?.toUpperCase() || 'N/A';
    // Build segment route including transit ports
    let mmSegRouteArr = [segment.origin || 'N/A'];
    if (segment.transits && segment.transits.length > 0) {
      mmSegRouteArr.push(...segment.transits);
    }
    mmSegRouteArr.push(segment.destination || 'N/A');
    const segmentRoute = mmSegRouteArr.join(' -> ');
    const mmSegHeaderText = `Segment ${index + 1}: ${segmentMode} (${segmentRoute})`;
    const mmSegHeaderLines = doc.splitTextToSize(mmSegHeaderText, 174);
    mmSegHeaderLines.forEach((line, li) => {
      if (li > 0) {
        doc.rect(15, yPos - 3, 180, 5, 'F');
      }
      doc.text(line, 18, yPos + 2);
      yPos += li === 0 ? 7 : 5;
    });

    // Incoterm display
    const mmCarriers = segment.carriers || [];
    const mmIncoterms = mmCarriers.map(c => c.incoterm).filter(Boolean);
    if (mmIncoterms.length > 0) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text(`Incoterm: ${mmIncoterms.join(', ')}`, 18, yPos);
      yPos += 5;
    }
    yPos += 3;

    // Freight Charges - Mutually exclusive with Ratio Charges for air segments
    const mmRatioTables = segment.seaFreightRatioChargesTables || [];
    const mmHasRatioData = segment.mode === 'air' && mmRatioTables.some(t =>
      (t.charges || []).some(c => c.carrier && isMeaningfulAmount(c.amount))
    );
    const mmHasFreightData = hasFreightChargesData(segment);

    if (segment.mode === 'air' && mmHasRatioData && !mmHasFreightData) {
      // Show ONLY ratio charges table for this air segment
      mmRatioTables.forEach((table) => {
        const ratioCharges = table.charges || [];
        if (ratioCharges.length > 0) {
          const tableName = table.tableName || 'Default';
          yPos = addAirFreightRatioChargesTable(doc, ratioCharges, yPos, `Air Freight Ratio Charges - ${tableName} (Segment ${index + 1})`);
        }
      });
    } else {
      // Show standard freight charges table
      if (segment.freightChargesTables && Array.isArray(segment.freightChargesTables) && segment.freightChargesTables.length > 0) {
        const hasMultipleTables = segment.freightChargesTables.length > 1 ||
                                   (segment.freightChargesTables[0]?.tableName && segment.freightChargesTables[0]?.tableName !== 'Default');

        if (hasMultipleTables) {
          segment.freightChargesTables.forEach((table, tableIndex) => {
            const tableCharges = table.charges || [];
            if (tableCharges.length > 0) {
              const tableName = table.tableName || `Table ${tableIndex + 1}`;
              yPos = addFreightChargesTableTransit(doc, tableCharges, yPos, segment.mode === 'air', index + 1, tableName);
            }
          });
        } else {
          const freightCharges = extractChargesFromTables(segment.freightChargesTables, 'freight');
          if (freightCharges.length > 0) {
            yPos = addFreightChargesTableTransit(doc, freightCharges, yPos, segment.mode === 'air', index + 1);
          }
        }
      } else if (segment.freightCharges) {
        const freightCharges = segment.freightCharges;
        if (freightCharges.length > 0) {
          yPos = addFreightChargesTableTransit(doc, freightCharges, yPos, segment.mode === 'air', index + 1);
        }
      }
    }

    // Origin Handling - extract from originHandlingTables
    let originHandling = [];
    if (segment.originHandlingTables && Array.isArray(segment.originHandlingTables)) {
      originHandling = extractChargesFromTables(segment.originHandlingTables, 'handling');
    } else if (segment.originHandling) {
      originHandling = segment.originHandling;
    }
    if (originHandling.length > 0) {
      yPos = addOtherChargesTable(doc, originHandling, `Seg${index + 1}: Origin Handling`, yPos);
    }

    // Destination Handling - extract from destinationHandlingTables
    let destinationHandling = [];
    if (segment.destinationHandlingTables && Array.isArray(segment.destinationHandlingTables)) {
      destinationHandling = extractChargesFromTables(segment.destinationHandlingTables, 'handling');
    } else if (segment.destinationHandling) {
      destinationHandling = segment.destinationHandling;
    }
    if (destinationHandling.length > 0) {
      yPos = addOtherChargesTable(doc, destinationHandling, `Seg${index + 1}: Destination Handling`, yPos);
    }

    // Destination Charges (POD) - extract from destinationChargesTables
    let destinationCharges = [];
    if (segment.destinationChargesTables && Array.isArray(segment.destinationChargesTables)) {
      destinationCharges = extractChargesFromTables(segment.destinationChargesTables, 'handling');
    } else if (segment.destinationCharges) {
      destinationCharges = segment.destinationCharges;
    }
    if (destinationCharges.length > 0) {
      yPos = addOtherChargesTable(doc, destinationCharges, `Seg${index + 1}: Destination Charges (POD)`, yPos);
    }
    
    yPos += 5;
  });
  
  
  // Terms
  if (terms.length > 0) {
    yPos += 10;
    if (yPos > 250) {
      doc.addPage();
      yPos = 20;
    }
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('Terms & Conditions:', 15, yPos);
    yPos += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    terms.forEach(term => {
      const lines = doc.splitTextToSize(`- ${term}`, 170);
      lines.forEach(line => {
        if (yPos > 280) {
          doc.addPage();
          yPos = 20;
        }
        doc.text(line, 15, yPos);
        yPos += 4;
      });
    });
  }
  
  // Footer
  const finalY = 280;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'italic');
  
  // Format user information
  const userName = userData ? `${userData.firstName || ''} ${userData.lastName || ''}`.trim() : 'System';
  const userEmail = userData?.email ? ` (${userData.email})` : '';
  
  doc.text(`Quotation generated by - ${userName}${userEmail}`, 15, finalY);
  doc.text('This is a Computer generated Document and no Signature required.', 15, finalY + 4);
  if (returnDoc) {
    return doc;
  }
  doc.save(`${quoteData.quoteNumber || 'MultiModal-Quote'}.pdf`);
};


// pdfPrintHelpers.js
// Add these print functions to your existing pdfGenerator.js file

/**
 * Generic function to print a PDF
 * Opens the PDF in a new window and triggers the browser's print dialog
 * @param {jsPDF} pdf - The jsPDF instance to print
 * @param {string} filename - Optional filename for the PDF
 */
export const printPDF = (pdf, filename = 'document.pdf') => {
  try {
    // Get the PDF as a blob
    const pdfBlob = pdf.output('blob');
    const blobUrl = URL.createObjectURL(pdfBlob);
    
    // Create an iframe to load the PDF
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    
    // Append iframe to body
    document.body.appendChild(iframe);
    
    // Set the source and trigger print when loaded
    iframe.src = blobUrl;
    iframe.onload = function() {
      setTimeout(() => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        
        // Clean up after printing or if user cancels
        setTimeout(() => {
          document.body.removeChild(iframe);
          URL.revokeObjectURL(blobUrl);
        }, 1000);
      }, 100);
    };
  } catch (error) {
    console.error('Error printing PDF:', error);
    toast.error('Failed to print PDF. Please try downloading instead.');
  }
};

/**
 * Print function for Direct Quote PDFs
 * Add this function to your pdfGenerator.js file alongside generateDirectQuotePDF
 */
export const printDirectQuotePDF = (quoteData, userData = null) => {
  try {
    // Generate PDF but return doc instead of saving
    const doc = generateDirectQuotePDF(quoteData, userData, true);
    printPDF(doc, `Quote_${quoteData.quoteNumber}_Direct.pdf`);
  } catch (error) {
    console.error('Error printing direct quote PDF:', error);
    toast.error('Failed to print PDF');
  }
};

export const printTransitQuotePDF = (quoteData, userData = null) => {
  try {
    // Generate PDF but return doc instead of saving
    const doc = generateTransitQuotePDF(quoteData, userData, true);
    printPDF(doc, `Quote_${quoteData.quoteNumber}_Transit.pdf`);
  } catch (error) {
    console.error('Error printing transit quote PDF:', error);
    toast.error('Failed to print PDF');
  }
};

export const printMultiModalQuotePDF = (quoteData, userData = null) => {
  try {
    // Generate PDF but return doc instead of saving
    const doc = generateMultiModalQuotePDF(quoteData, userData, true);
    printPDF(doc, `Quote_${quoteData.quoteNumber}_MultiModal.pdf`);
  } catch (error) {
    console.error('Error printing multi-modal quote PDF:', error);
    toast.error('Failed to print PDF');
  }
};

/**
 * Print function for Warehouse Quote PDFs
 * Add this function to your warehousePDFGenerator.js file
 */
// export const printWarehouseQuotePDF = async (quoteData) => {
//   try {
//     const pdf = await generateWarehouseQuotePDFObject(quoteData); // Modified version that returns pdf
//     printPDF(pdf, `Quote_${quoteData.quoteNumber}_Warehouse.pdf`);
//   } catch (error) {
//     console.error('Error printing warehouse quote PDF:', error);
//     toast.error('Failed to print PDF');
//   }
// };

/**
 * IMPORTANT: Refactoring Instructions
 * 
 * To implement these print functions, you need to refactor your existing PDF generation functions:
 * 
 * 1. Keep your existing generateXXXQuotePDF functions that call pdf.save()
 * 2. Create new generateXXXQuotePDFObject functions that return the pdf object
 * 3. Both functions should share the same PDF generation logic
 * 
 * Example refactoring pattern:
 * 
 * // Shared PDF generation logic
 * const buildDirectQuotePDF = async (quoteData) => {
 *   const pdf = new jsPDF(...);
 *   // ... all your PDF generation code ...
 *   return pdf;
 * };
 * 
 * // For download (existing function)
 * export const generateDirectQuotePDF = async (quoteData) => {
 *   const pdf = await buildDirectQuotePDF(quoteData);
 *   pdf.save(`Quote_${quoteData.quoteNumber}_Direct.pdf`);
 * };
 * 
 * // For print (new function)
 * export const generateDirectQuotePDFObject = async (quoteData) => {
 *   return await buildDirectQuotePDF(quoteData);
 * };
 * 
 * // Then use printPDF helper
 * export const printDirectQuotePDF = async (quoteData) => {
 *   const pdf = await generateDirectQuotePDFObject(quoteData);
 *   printPDF(pdf, `Quote_${quoteData.quoteNumber}_Direct.pdf`);
 * };
 */

// Alternative simpler approach if you don't want to refactor:
// Modify your existing generate functions to accept a "mode" parameter

/**
 * Alternative: Modified Direct Quote PDF generator with mode parameter
 */
export const generateDirectQuotePDF_Alternative = async (quoteData, mode = 'download') => {
  // ... all your PDF generation code ...
  const pdf = new jsPDF(/* your config */);
  
  // ... build the PDF ...
  
  if (mode === 'download') {
    pdf.save(`Quote_${quoteData.quoteNumber}_Direct.pdf`);
  } else if (mode === 'print') {
    printPDF(pdf, `Quote_${quoteData.quoteNumber}_Direct.pdf`);
  }
  
  return pdf; // Return for additional processing if needed
};

// Then in your component:
// Download: generateDirectQuotePDF_Alternative(pdfData, 'download');
// Print: generateDirectQuotePDF_Alternative(pdfData, 'print');
